import {
  Badge,
  Box,
  BoxProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import React from "react";
import {Close} from "@mui/icons-material";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {WorkClassDocument, WorkInstanceDocument, WorkModelDocument} from "../../../../types/pj1dbApiWork";
import {FetchMessage, Status, STATUSES} from "../../../../hooks/useFetchAllQuery";
import SearchForm, {SearchCond} from "./SearchForm";
import SortOrder from "./SortOrder";
import {WORK_SORT_ORDER, WorkSortOrderValue} from "../../../../constants/constants";
import WorkModelListItem from "../lists/WorkModelListItem";
import WorkClassListItem from "../lists/WorkClassListItem";
import WorkInstanceListItem from "../lists/WorkInstanceListItem";
import CloseIconButton from "../buttons/CloseIconButton";
import Pjdb1ToolTip from "../../../../components/inputs/Pjdb1ToolTip";

export const WORK_MODEL_SELECTION_STEP = {
  CLASS: 'class',
  INSTANCE: 'instance',
  MODEL: 'model',
} as const;
export type WorkModelSelectionStep = typeof WORK_MODEL_SELECTION_STEP[keyof typeof WORK_MODEL_SELECTION_STEP];

export interface WorkModelSelectionFieldProps extends WorkClassSearchFormProps,
  Omit<WorkClassListProps, 'onClick'>,
  Omit<WorkInstanceListProps, 'onClick' | 'selectedWorkClass'>,
  Omit<WorkModelListProps, 'onClick' | 'selectedWorkInstance'> {
  step: WorkModelSelectionStep;
  onChangeClass: (workClasses: WorkClassDocument | null) => void;
  onChangeInstance: (workInstances: WorkInstanceDocument | null) => void;
  onChangeModels: (workModels: WorkModelDocument[]) => void;
}

const selectedStyle: BoxProps['sx'] = {
  outlineColor: (theme) => theme.palette.primary.light,
  outlineStyle: "solid",
  outlineWidth: 6,
  borderRadius: 1,
};

export function WorkModelSelectionField({onChangeModels, selectedWorkModels, ...props}: WorkModelSelectionFieldProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Stack gap={3}>
        <Box display='flex' flexWrap='nowrap' gap={2}>
          <Pjdb1Button
            label='対象物を選択する'
            variant='outlined'
            color='inherit'
            onClick={() => setOpen(true)}
            sx={{width: 'fit-content'}}
          />
          <Pjdb1ToolTip
            toolTip='対象物を選択するを押下し、カスタムワールドに配置する対象物を検索して選択してください。'/>
        </Box>

        <Box display='flex' flexWrap='wrap' width='100%' gap={3}>
          {selectedWorkModels.map((workModel) => (
            <Box key={workModel.attr['model-id']}>
              <Badge
                badgeContent={
                  <Close
                    fontSize='medium'
                    onClick={() => onChangeModels(selectedWorkModels.filter((o) => o.attr["model-id"] !== workModel.attr["model-id"]))}
                    sx={{color: 'white', bgcolor: 'gray', borderRadius: 4}
                    }/>
                }>
                <WorkModelListItem item={workModel}/>
              </Badge>
            </Box>
          ))}
        </Box>
      </Stack>
      <Dialog disableEscapeKeyDown open={open} onClose={() => setOpen(false)} fullWidth maxWidth='xl'>
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            対象物を選択
            <CloseIconButton onClose={() => setOpen(false)}/>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack bgcolor='#F6F7F8' py={4} px={2} gap={4}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <WorkClassSearchForm {...props}/>

            {props.step === WORK_MODEL_SELECTION_STEP.CLASS && (
              <WorkClassList
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...props}
                onClick={(workClass) => {
                  props.onChangeClass(workClass);
                  props.onChangeStep(WORK_MODEL_SELECTION_STEP.INSTANCE);
                }}
              />
            )}
            {props.step === WORK_MODEL_SELECTION_STEP.INSTANCE && props.selectedWorkClass && (
              <WorkInstanceList
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...props}
                onClick={(workInstance) => {
                  props.onChangeInstance(workInstance);
                  props.onChangeStep(WORK_MODEL_SELECTION_STEP.MODEL);
                }}
                onChangeStep={props.onChangeStep}
              />
            )}
            {props.step === WORK_MODEL_SELECTION_STEP.MODEL && props.selectedWorkInstance && (
              <WorkModelList
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...props}
                selectedWorkModels={selectedWorkModels}
                onClick={(workModel) => {
                  if (selectedWorkModels.some((o) => o.attr['model-id'] === workModel.attr['model-id'])) {
                    onChangeModels(selectedWorkModels.filter((o) => o.attr['model-id'] !== workModel.attr['model-id']));
                    return;
                  }
                  onChangeModels([...selectedWorkModels, workModel]);
                }}
                onChangeStep={props.onChangeStep}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Pjdb1Button label="閉じる" color='inherit' variant='contained' onClick={() => setOpen(false)}/>
        </DialogActions>
      </Dialog>
    </>
  )
}

interface WorkClassSearchFormProps {
  searchCond: SearchCond<WorkSortOrderValue>;
  onChangeSearchCond: (searchCond: SearchCond<WorkSortOrderValue>) => void;
  fetchWorkClasses: () => Promise<void>;
  searchWorkClassStatus: Status;
  onChangeStep: (step: WorkModelSelectionStep) => void;
}

function WorkClassSearchForm(props: WorkClassSearchFormProps) {
  return (
    <SearchForm
      label="対象物名、JAN/部品コード、メーカーなどを入力"
      isLoading={props.searchWorkClassStatus === STATUSES.FETCHING}
      searchCond={props.searchCond}
      onChange={props.onChangeSearchCond}
      onSubmit={(e) => {
        e.preventDefault();
        props.onChangeStep(WORK_MODEL_SELECTION_STEP.CLASS);
        props.fetchWorkClasses().catch(console.error);
      }}
    />
  );
}

interface WorkClassListProps {
  workClasses: WorkClassDocument[];
  selectedWorkClass: WorkClassDocument | null;
  searchCond: SearchCond<WorkSortOrderValue>;
  onChangeSearchCond: (searchCond: SearchCond<WorkSortOrderValue>) => void;
  searchWorkClassStatus: Status;
  onClick: (workClass: WorkClassDocument) => void;
}

function WorkClassList(props: WorkClassListProps) {
  return (
    <>
      <Box display='flex' flexWrap='nowrap' justifyContent='space-between'>
        <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
          検索結果一覧
        </Typography>
        <Box display='flex' gap={2}>
          <Box>
            {props.workClasses.length > 0 ? `${props.workClasses.length.toLocaleString()}件` : ""}
          </Box>
          <SortOrder
            value={props.searchCond.sortOrder}
            onChange={(event) => {
              props.onChangeSearchCond({
                ...props.searchCond,
                sortOrder: event.target.value as WorkSortOrderValue
              });
            }}
            items={Object.values(WORK_SORT_ORDER)}/>
        </Box>
      </Box>

      {props.workClasses.length > 0 && (
        <Box display='flex' flexWrap='wrap' gap={2}>
          {props.workClasses.map((workClass) => (
            <WorkClassListItem
              key={workClass.attr["class-id"]}
              item={workClass}
              onClick={() => props.onClick(workClass)}
              sx={{
                outlineColor: 'inherit',
                outlineStyle: "solid",
                outlineWidth: 1,
                borderRadius: 1,
                ...(
                  props.selectedWorkClass?.attr["class-id"] === workClass.attr["class-id"]
                    ? selectedStyle
                    : {}
                )
              }}
            />
          ))}
        </Box>
      )}
      <FetchMessage data={props.workClasses} status={props.searchWorkClassStatus}/>
    </>
  )
}

interface WorkInstanceListProps {
  workInstances: WorkInstanceDocument[];
  selectedWorkInstance: WorkInstanceDocument | null;
  searchWorkInstanceStatus: Status;
  onClick: (workInstance: WorkInstanceDocument) => void;
  onChangeStep: (step: WorkModelSelectionStep) => void;
}

function WorkInstanceList(props: WorkInstanceListProps) {
  return (
    <>
      <Box display='flex' flexWrap='nowrap' justifyContent='space-between'>
        <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
          検索結果一覧
        </Typography>
        <Pjdb1Button
          label='対象物の選択'
          variant='text'
          color='inherit'
          onClick={() => props.onChangeStep(WORK_MODEL_SELECTION_STEP.CLASS)}
        />
      </Box>
      {props.workInstances.length > 0 && (
        <Box display='flex' flexWrap='wrap' gap={2}>
          {props.workInstances.map((workInstance) => (
            <WorkInstanceListItem
              key={workInstance.attr["instance-id"]}
              item={workInstance}
              onClick={() => props.onClick(workInstance)}
              sx={{
                outlineColor: 'inherit',
                outlineStyle: "solid",
                outlineWidth: 1,
                borderRadius: 1,
                ...(
                  props.selectedWorkInstance?.attr["instance-id"] === workInstance.attr["instance-id"]
                    ? selectedStyle
                    : {}
                )
              }}
            />
          ))}
        </Box>
      )}
      <FetchMessage data={props.workInstances} status={props.searchWorkInstanceStatus}/>
    </>
  )
}


interface WorkModelListProps {
  workModels: WorkModelDocument[];
  selectedWorkModels: WorkModelDocument[];
  onClick: (workModel: WorkModelDocument) => void;
  searchWorkModelStatus: Status;
  onChangeStep: (step: WorkModelSelectionStep) => void;
}

function WorkModelList(props: WorkModelListProps) {
  return (
    <>
      <Box display='flex' flexWrap='nowrap' justifyContent='space-between'>
        <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
          検索結果一覧
        </Typography>
        <Pjdb1Button
          label='サンプルの選択'
          variant='text'
          color='inherit'
          onClick={() => props.onChangeStep(WORK_MODEL_SELECTION_STEP.INSTANCE)}
        />
      </Box>
      {props.workModels.length > 0 && (
        <Box display='flex' flexWrap='wrap' gap={2}>
          {props.workModels.map((workModel) => (
            <WorkModelListItem
              key={workModel.attr["model-id"]}
              item={workModel}
              onClick={() => props.onClick(workModel)}
              sx={{
                outlineColor: 'inherit',
                outlineStyle: "solid",
                outlineWidth: 1,
                borderRadius: 1,
                ...(
                  props.selectedWorkModels.some((o) => o.attr["model-id"] === workModel.attr["model-id"])
                    ? selectedStyle
                    : {}
                )
              }}
            />
          ))}
        </Box>
      )}

      <FetchMessage data={props.workModels} status={props.searchWorkModelStatus}/>
    </>
  )
}
